import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
	Stack,
	Pagination,
	TextField,
	InputAdornment,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Button,
	Box,
	Tooltip,
	ToggleButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import Page from '../../components/Page';
import CodeIcon from '@mui/icons-material/Code';
import { toWapi } from '../../util/api';

class GroupActionLogs extends Component {
	state = {
		groupData: null,
		loading: true,
		error: null,
		currentPage: 1,
		itemsPerPage: 50,
		searchTerm: '',
		phones: [],
		selectedPhoneId: '',
		selectedAction: 'remove',
		loadingFilter: false,
		currentPageLimit: 500,
		showConnectedOnly: true,
	};

	componentDidMount() {
		this.fetchPhones();
		this.fetchGroupActionLogs();
	}

	fetchPhones = async () => {
		const { product } = this.props;

		try {
			const path = `/api/${product.id}/listPhones`;
			const response = await toWapi(path, product.settings.wapi_token);

			if (response.data) {
				this.setState({
					phones: response.data,
				});
			}
		} catch (error) {
			console.error('Error fetching phones:', error);
		}
	};

	fetchWithOptimalLimit = async (phoneId = null, action = 'remove') => {
		const { product } = this.props;
		
		const makeRequest = async (limit) => {
			const path = phoneId
				? `/api/${product.id}/${phoneId}/groupInfo?action=${action}&pageLimit=${limit}`
				: `/api/${product.id}/groupInfo?action=${action}&pageLimit=${limit}`;

			const response = await toWapi(path, product.settings.wapi_token);
			
			if (response.data && response.data.success) {
				return response.data.data.list;
			} else {
				throw new Error('Failed to fetch group information');
			}
		};

		try {
			let resultData = await makeRequest(500);
			if (resultData.length === 500) {
				return resultData;
			}
			if (resultData.length < 500) {
				resultData = await makeRequest(450);
				if (resultData.length < 450) {
					resultData = await makeRequest(400);
					return resultData;
				}
				return resultData;
			}
			return resultData;
		} catch (error) {
			console.error(error);
		}
	};

	fetchGroupActionLogs = async (phoneId = null, action = 'remove') => {
		try {
			this.setState({ loadingFilter: true });
			
			const resultData = await this.fetchWithOptimalLimit(phoneId, action);

			this.setState({
				groupData: resultData,
				loading: false,
				loadingFilter: false,
				error: null,
			});
		} catch (error) {
			console.error('Error fetching group info:', error);
			this.setState({
				loading: false,
				loadingFilter: false,
				error: error.message || 'An error occurred while fetching data',
			});
		}
	};

	handlePageChange = (event, value) => {
		this.setState({ currentPage: value });
	};

	handleSearchChange = (event) => {
		this.setState({ searchTerm: event.target.value, currentPage: 1 });
	};

	handlePhoneSelectChange = (event) => {
		const phoneId = event.target.value;
		this.setState({ 
			selectedPhoneId: phoneId,
			currentPage: 1
		});

		const { selectedAction } = this.state;
		if (phoneId) {
			this.fetchGroupActionLogs(phoneId, selectedAction);
		} else {
			this.fetchGroupActionLogs(null, selectedAction);
		}
	};

	handleActionSelectChange = (event) => {
		const action = event.target.value;
		this.setState({ 
			selectedAction: action,
			currentPage: 1
		});

		const { selectedPhoneId } = this.state;
		if (selectedPhoneId) {
			this.fetchGroupActionLogs(selectedPhoneId, action);
		} else {
			this.fetchGroupActionLogs(null, action);
		}
	};

	handleShowAll = () => {
		this.setState({
			selectedPhoneId: '',
			selectedAction: 'remove',
			currentPage: 1,
			showConnectedOnly: false,
		});
		this.fetchGroupActionLogs(null, 'remove');
	};

	handleConnectedToggle = () => {
		this.setState(prevState => ({
			showConnectedOnly: !prevState.showConnectedOnly,
			currentPage: 1
		}));
	};

	downloadCSV = () => {
		const { groupData } = this.state;
		const filteredData = this.filterData(groupData || []);

		if (!filteredData.length) return;

		const headers = ['Phone ID', 'Number', 'Group Name', 'Group ID', 'Participant', 'Subtype', 'Date'];

		const csvData = filteredData.map((item) => [
			item.phone,
			item.data.body.receiver || 'N/A',
			item.data.body.conversation_name || 'N/A',
			item.data.body.conversation || 'N/A',
			item.data.body.message.participant || 'N/A',
			item.data.body.message.subtype || 'N/A',
			this.formatDate(item.created_at),
		]);

		const csvContent = [headers, ...csvData].map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `group-action-logs-${new Date().toISOString().split('T')[0]}.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	filterData = (data) => {
		const { searchTerm, showConnectedOnly } = this.state;
		let filteredData = data;

		if (searchTerm) {
			filteredData = filteredData.filter(
				(item) =>
					item.phone.toString().includes(searchTerm) ||
					(item.data.body.conversation_name && item.data.body.conversation_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
					(item.data.body.message.participant && item.data.body.message.participant.includes(searchTerm)) ||
					(item.data.body.receiver && item.data.body.receiver.includes(searchTerm))
			);
		}

		if (showConnectedOnly) {
			filteredData = filteredData.filter(item => {
				const userPhone = item.data.body.receiver;
				const participant = item.data.body.message.participant;
				return this.isConnectedNumber(userPhone, participant);
			});
		}

		return filteredData;
	};

	formatDate = (dateString) => {
		const date = new Date(dateString);
		const year = date.getUTCFullYear();
		const month = String(date.getUTCMonth() + 1).padStart(2, '0');
		const day = String(date.getUTCDate()).padStart(2, '0');
		const hour = String(date.getUTCHours()).padStart(2, '0');
		const minute = String(date.getUTCMinutes()).padStart(2, '0');
		const second = String(date.getUTCSeconds()).padStart(2, '0');

		return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
	};

	isConnectedNumber = (userPhone, participant) => {
		if (!userPhone || !participant) return false;
		const cleanParticipant = participant.replace('@c.us', '');
		return userPhone === cleanParticipant;
	};

	render() {
		const { groupData, loading, error, currentPage, itemsPerPage, searchTerm, phones, selectedPhoneId, selectedAction, loadingFilter, showConnectedOnly } = this.state;

		if (loading) {
			return (
				<Page title="Developers" subtitle="Group Action Logs" icon={<CodeIcon />}>
					<Stack sx={{ marginTop: '20%', marginLeft: '50%' }} attached="bottom">
						<CircularProgress disableShrink />
					</Stack>
				</Page>
			);
		}

		if (error) {
			return (
				<Page title="Developers" subtitle="Group Action Logs" icon={<CodeIcon />}>
					<Typography variant="h6" color="error" sx={{ textAlign: 'center', mt: 4 }}>
						An unexpected error occurred.
					</Typography>
				</Page>
			);
		}

		const filteredData = this.filterData(groupData || []);
		const indexOfLastItem = currentPage * itemsPerPage;
		const indexOfFirstItem = indexOfLastItem - itemsPerPage;
		const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
		const pageCount = Math.ceil(filteredData.length / itemsPerPage);

		return (
			<Page title="Developers" subtitle="Group Action Logs" icon={<CodeIcon />}>
				<Stack spacing={2}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
							<FormControl sx={{ minWidth: 200, mt: 1 }}>
								<InputLabel>Filter by Phone</InputLabel>
								<Select
									value={selectedPhoneId}
									label="Filter by Phone"
									onChange={this.handlePhoneSelectChange}
									disabled={loadingFilter}
									MenuProps={{
										PaperProps: {
											style: {
												maxHeight: 48 * 4.5,
												width: 250,
											},
										},
									}}
								>
									{phones
										.sort((a, b) => a.id - b.id)
										.map((phone) => (
											<MenuItem key={phone.id} value={phone.id}>
												{phone.id} {phone.number ? `(${phone.number})` : `(${phone.status})`}
											</MenuItem>
										))}
								</Select>
							</FormControl>

							<FormControl sx={{ minWidth: 150, mt: 1 }}>
								<InputLabel>Filter by Subtype</InputLabel>
								<Select value={selectedAction} label="Filter by Subtype" onChange={this.handleActionSelectChange} disabled={loadingFilter}>
									<MenuItem value="add">Add</MenuItem>
									<MenuItem value="demote">Demote</MenuItem>
									<MenuItem value="invite">Invite</MenuItem>
									<MenuItem value="leave">Leave</MenuItem>
									<MenuItem value="name">Name</MenuItem>
									<MenuItem value="promote">Promote</MenuItem>
									<MenuItem value="remove">Remove</MenuItem>
								</Select>
							</FormControl>

							<Button variant="outlined" onClick={this.handleShowAll} disabled={loadingFilter} sx={{ mt: 1 }}>
								Clear
							</Button>

							<Tooltip title={showConnectedOnly ? "Showing only connected numbers" : "Show only connected numbers"} arrow>
								<ToggleButton
									value="connected"
									selected={showConnectedOnly}
									onChange={this.handleConnectedToggle}
									disabled={loadingFilter}
									sx={{ 
										mt: 1,
										height: '40px',
										minWidth: '50px',
									}}
									color="primary"
								>
									{showConnectedOnly ? <LinkIcon /> : <LinkOffIcon />}
								</ToggleButton>
							</Tooltip>

							{loadingFilter && <CircularProgress size={24} sx={{ mt: 1 }} />}
						</Box>

						<Button
							variant="contained"
							startIcon={<DownloadIcon />}
							onClick={this.downloadCSV}
							disabled={!filteredData.length || loadingFilter}
							sx={{ mt: 1 }}
						>
							Download CSV
						</Button>
					</Box>

					<TextField
						fullWidth
						variant="outlined"
						placeholder="Search by Phone ID, Group Name, Participant, or Number"
						value={searchTerm}
						onChange={this.handleSearchChange}
						sx={{ mb: 2 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>

					<TableContainer component={Paper}>
						<Table sx={{ background: '#fafafa' }}>
							<TableHead>
								<TableRow>
									<TableCell>
										<strong>Phone ID</strong>
									</TableCell>
									<TableCell>
										<strong>Number</strong>
									</TableCell>
									<TableCell>
										<strong>Group Name</strong>
									</TableCell>
									<TableCell>
										<strong>Group ID</strong>
									</TableCell>
									<TableCell>
										<strong>Participant</strong>
									</TableCell>
									<TableCell>
										<strong>Subtype</strong>
									</TableCell>
									<TableCell>
										<strong>Date</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!currentItems.length ? (
									<TableRow>
										<TableCell style={{ textAlign: 'center' }} colSpan="7">
											No matching results found.
										</TableCell>
									</TableRow>
								) : (
									currentItems.map((item, index) => {
										const userPhone = item.data.body.receiver;
										const participant = item.data.body.message.participant;
										const isConnected = this.isConnectedNumber(userPhone, participant);

										return (
											<TableRow key={`${item.phone}-${index}`}>
												<TableCell>{item.phone}</TableCell>
												<TableCell>{userPhone || 'N/A'}</TableCell>
												<TableCell>{item.data.body.conversation_name || 'N/A'}</TableCell>
												<TableCell>{item.data.body.conversation || 'N/A'}</TableCell>
												<TableCell>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														{participant || 'N/A'}
														{isConnected && (
															<Tooltip title="This is related to your connected number." arrow>
																<InfoIcon sx={{ color: '#1976d2', fontSize: 18 }} />
															</Tooltip>
														)}
													</Box>
												</TableCell>
												<TableCell>{item.data.body.message.subtype || 'N/A'}</TableCell>
												<TableCell>{this.formatDate(item.created_at)}</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{pageCount > 1 && (
						<Stack spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
							<Pagination count={pageCount} page={currentPage} onChange={this.handlePageChange} color="primary" showFirstButton showLastButton />
						</Stack>
					)}

					<Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
						Total Records: {filteredData.length}
					</Typography>
				</Stack>
			</Page>
		);
	}
}

export default connect((state) => ({ user: state.access.user.loggedUserObj, product: state.product.maytapi }))(GroupActionLogs);